import { canonicalIdBase } from '@core-mkt/services/schema/schema-id.util';
import type { SchemaOrgNode } from '@core-mkt/services/schema/schema-org-node.model';
import type { StateCourseMapping } from './popular-courses';

/**
 * Builds a Schema.org ItemList node referencing each popular course by URN.
 *
 * The component only renders the first entry of `courseOne` and `courseTwo` in
 * the resolved state mapping, so the schema mirrors that — at most two list
 * items, deduped by `xgritProductId`. Course `@id` nodes are auto-registered
 * elsewhere by the page-data resolver, so this fragment is purely the wrapper.
 *
 * Returns `null` when there's nothing to register: missing mapping, no entries
 * in either slot, or every entry lacks an `xgritProductId`.
 */
export function getPopularCoursesSchema(
  mapping: StateCourseMapping | null | undefined,
  pageUrl: string,
): SchemaOrgNode | null {
  if (!mapping) {
    return null;
  }

  const candidateIds = [mapping.courseOne?.[0]?.xgritProductId, mapping.courseTwo?.[0]?.xgritProductId];

  const seen = new Set<string>();
  const ids: string[] = [];
  for (const id of candidateIds) {
    // Empty/missing ids are dropped; '0' is preserved (string semantics).
    if (id === undefined || id === null || id === '') continue;
    if (seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
  }

  if (ids.length === 0) {
    return null;
  }

  const itemListElement = ids.map((id, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    item: { '@id': `urn:xgrit:course:${id}` },
  }));

  return {
    '@type': 'ItemList',
    '@id': `${canonicalIdBase(pageUrl)}#popular-courses`,
    numberOfItems: ids.length,
    itemListElement,
  };
}
